import { useState, useEffect } from "react"
import { Trash2, Key } from "lucide-react"
import { format } from "date-fns"
import { useTranslation } from "react-i18next"
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter"
import { coldarkDark as codeStyle } from "react-syntax-highlighter/dist/esm/styles/prism"

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@f2e/ui/components/base"

import { AlertBox } from "@/components"
import { OnboardingEmptyState } from "@/components/System"
import { PermissionButton } from "@/components/Auth"
import { Permission, Workspace, WorkspaceApiKey } from "@/types"
import { useToast } from "@/hooks"
import { FetchStatus, useRevokeWorkspaceApiKeyApi } from "@/hooks/api"
import { getApiErrorData, getUserFacingApiErrorMessage } from "@/lib/apiErrors"

const maskApiKeyPrefix = (prefix: string): string => {
  return `${prefix}.****...`
}

const isExpired = (expiresAt?: Date): boolean => {
  if (!expiresAt) return false
  return new Date() > expiresAt
}

export interface ApiKeysTableProps {
  workspace: Workspace
  apiKeys: WorkspaceApiKey[]
  onRevoke?: (prefix: string) => void
  onIssueNew?: () => void
}

export const ApiKeysTable = ({
  workspace,
  apiKeys,
  onRevoke,
  onIssueNew,
}: ApiKeysTableProps) => {
  const { t } = useTranslation()
  const { successToast, alertToast } = useToast()

  const [showRevokeDialog, setShowRevokeDialog] = useState(false)
  const [selectedPrefix, setSelectedPrefix] = useState<string>("")

  // APIs
  const {
    data: revokeKeyRespData,
    fetchFn: revokeApiKey,
    status: revokeKeyRespStatus,
    error: revokeKeyRespError,
  } = useRevokeWorkspaceApiKeyApi(
    workspace.organizationId || "",
    workspace.id,
    selectedPrefix
  )

  // Handle revoke key response
  useEffect(() => {
    if (!revokeKeyRespData) return
    successToast({
      title: t("workspace.apiKeys.messages.revoked.title"),
      description: t("workspace.apiKeys.messages.revoked.description"),
    })
  }, [revokeKeyRespData, successToast, t])

  // Handle revoke key error
  useEffect(() => {
    if (!revokeKeyRespError) return
    console.error(
      "Failed to revoke API key.",
      getApiErrorData(revokeKeyRespError)?.message || revokeKeyRespData?.message
    )
    alertToast({
      title: t("workspace.apiKeys.messages.error.revokeKey.title"),
      description: getUserFacingApiErrorMessage(
        revokeKeyRespError,
        t,
        t("workspace.apiKeys.messages.error.revokeKey.description")
      ),
    })
  }, [revokeKeyRespError, revokeKeyRespData, alertToast, t])

  const handleRevokeClick = (prefix: string) => {
    setSelectedPrefix(prefix)
    setShowRevokeDialog(true)
  }

  const handleConfirmRevoke = () => {
    revokeApiKey()
      .then(() => {
        onRevoke?.(selectedPrefix)
        setShowRevokeDialog(false)
        setSelectedPrefix("")
      })
      .catch(() => undefined)
  }

  if (apiKeys.length === 0) {
    return (
      <OnboardingEmptyState
        icon={Key}
        title={t("workspace.apiKeys.onboarding.title")}
        description={t("workspace.apiKeys.onboarding.description")}
        primaryAction={
          onIssueNew
            ? {
                label: t("workspace.apiKeys.onboarding.cta"),
                onClick: onIssueNew,
                permission: {
                  required: Permission.WsUpdateResources,
                  workspace,
                  disabledTooltip: t("common.noPermission"),
                },
              }
            : undefined
        }
        learnMoreUrl={t("workspace.apiKeys.onboarding.examplesUrl")}
        learnMoreLabel={t("workspace.apiKeys.onboarding.viewExamples")}
      >
        {/* Code snippet example */}
        <div className="w-full max-w-2xl text-left">
          <SyntaxHighlighter
            language="bash"
            style={codeStyle}
            customStyle={{
              borderRadius: "0.5rem",
              padding: "1.25rem",
              fontSize: "0.8125rem",
              lineHeight: "1.6",
            }}
          >
            {`# 1. Create a chat session
curl -X POST https://api.codeer.ai/api/v1/chats \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"agent_id": "your_agent_id"}'

# 2. Send a message (replace {chat_id} with the id from step 1)
curl -X POST https://api.codeer.ai/api/v1/chats/{chat_id}/messages \\
  -H "x-api-key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"message": "Hello!", "stream": false}'`}
          </SyntaxHighlighter>
        </div>
      </OnboardingEmptyState>
    )
  }

  return (
    <>
      <div className="rounded-md border border-slate-200 dark:border-slate-800">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("workspace.apiKeys.table.name")}</TableHead>
              <TableHead>{t("workspace.apiKeys.table.prefix")}</TableHead>
              <TableHead>{t("workspace.apiKeys.table.role")}</TableHead>
              <TableHead>{t("workspace.apiKeys.table.createdAt")}</TableHead>
              <TableHead>{t("workspace.apiKeys.table.expiresAt")}</TableHead>
              <TableHead className="w-[100px]">
                {t("workspace.apiKeys.table.actions")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {apiKeys.map((apiKey) => {
              const expired = isExpired(apiKey.expiresAt)
              return (
                <TableRow key={apiKey.prefix}>
                  <TableCell className="font-medium">{apiKey.name}</TableCell>
                  <TableCell className="font-mono text-sm">
                    {maskApiKeyPrefix(apiKey.prefix)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        apiKey.role === "admin" ? "outline" : "secondary"
                      }
                      className={
                        apiKey.role === "admin"
                          ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
                          : undefined
                      }
                    >
                      {t(`workspace.apiKeys.roles.${apiKey.role}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {format(apiKey.createdAt, "MMM dd, yyyy")}
                  </TableCell>
                  <TableCell>
                    {apiKey.expiresAt ? (
                      <div className="flex items-center gap-2">
                        <span>{format(apiKey.expiresAt, "MMM dd, yyyy")}</span>
                        {expired && (
                          <Badge variant="destructive" className="text-xs">
                            Expired
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-500 dark:text-slate-400">
                        {t("workspace.apiKeys.table.neverExpires")}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <PermissionButton
                      permission={Permission.WsUpdateResources}
                      workspace={workspace}
                      disabledTooltip={t("common.noPermission")}
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRevokeClick(apiKey.prefix)}
                      disabled={revokeKeyRespStatus === FetchStatus.Loading}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </PermissionButton>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <AlertBox
        open={showRevokeDialog}
        onOpenChange={setShowRevokeDialog}
        title={t("workspace.apiKeys.revokeDialog.title")}
        description={t("workspace.apiKeys.revokeDialog.description")}
        cancelText={t("workspace.apiKeys.revokeDialog.cancel")}
        confirmText={
          revokeKeyRespStatus === FetchStatus.Loading
            ? t("workspace.apiKeys.issueDialog.issuing")
            : t("workspace.apiKeys.revokeDialog.revoke")
        }
        onConfirm={handleConfirmRevoke}
        onCancel={() => setShowRevokeDialog(false)}
      />
    </>
  )
}

export default ApiKeysTable
